// Fleet desk modules. Each face is a summary tile fed by the SAME TanStack
// query keys/options as the full panel behind its drawer, so opening the
// drawer is a cache hit. Faces are state-only: no buttons or links inside.
//
// Loading rule (plan Review Focus 2, see useBooks.ts): a query that is
// `isLoading` OR (`isPaused && data === undefined`) is still LOADING — never
// a resolved zero. While loading, faces render '—' and omit compare/footer so
// no `$0` / `0/25` / `NaN` can leak.
import { useQueries, useQuery } from '@tanstack/react-query'
import { accountApi, benchmarkApi } from '../../api/client'
import { useTimeframe } from '../../hooks/useTimeframe'
import { bookWindowReturn } from '../../lib/pickPerformance'
import { timeframeLabel, type Timeframe } from '../../lib/timeframe'
import { FleetValueChart, useFleetIndexedSeries } from '../FleetValueChart'
import { HBarPair } from '../viz/HBarPair'
import { Module } from './Module'
import {
  bookCountMarker,
  liveBooks,
  num,
  stillLoading,
  unavailable,
  usePicksCaveats,
  type FleetModuleProps,
} from './fleetModuleHooks'
import { excessByHorizon } from './fleetSummaries'

// ------------------------------------------------------------- fleet value

/**
 * Row-2 hero: every non-dormant book indexed to 100 at a common base date
 * (useFleetIndexedSeries, ranged by the global timeframe). The module's own
 * legend replaces recharts' Legend to save vertical space. No SPY line exists
 * in phase 1, so there is no SPY legend entry.
 */
export function FleetValueModule({ books, booksPending, booksError }: FleetModuleProps) {
  const { timeframe } = useTimeframe()
  const live = liveBooks(books)
  const { rows, lines, excluded, isLoading: seriesLoading, isError: seriesError } = useFleetIndexedSeries(live)

  const isLoading = booksPending || seriesLoading
  // The hook's isError ignores cached data; a failed background refetch that
  // still has comparable rows keeps the chart (unavailable = error AND no data).
  const isError = !isLoading && ((seriesError && rows.length < 2) || (booksError && live.length === 0))

  return (
    <Module
      title="Fleet value"
      // DAY charts the 1W range (toEquityRange): FleetValueOverlay's wording.
      caption={timeframe === 'DAY' ? 'indexed · 1W · day view' : `indexed · ${timeframeLabel(timeframe)}`}
      drawerKey="fleet-value"
      span={7}
      isLoading={isLoading}
      isError={isError}
      errorText="history unavailable"
      isEmpty={rows.length < 2}
      emptyText="no comparable history across books yet"
    >
      <div className="module-legend">
        {lines.map((l) => (
          <span key={l.key}>
            <i style={{ background: l.color }} />
            {l.label}
          </span>
        ))}
        {excluded.length > 0 && (
          <em className="warn" title={excluded.join('; ')}>
            {bookCountMarker(excluded.length, 'excluded')}
          </em>
        )}
      </div>
      <FleetValueChart rows={rows} lines={lines} fill showLegend={false} />
    </Module>
  )
}

// ---------------------------------------------------------- picks horizons

const HORIZON_WINDOWS = [
  ['1w', '1W'],
  ['1m', '1M'],
  ['3m', '3M'],
] as const satisfies ReadonlyArray<readonly [string, Timeframe]>

/**
 * Research book's return minus SPY's at 1D/1W/1M/3M (no YTD: the benchmark
 * endpoint has no YTD window). Same keys/options as PicksPanel so the drawer
 * is a cache hit. A benchmark or window failure degrades its row to '—';
 * only a failed account read is an error.
 */
export function PicksHorizonsModule({ books, booksPending, booksError }: FleetModuleProps) {
  const research = books.find((b) => b.role === 'research')
  const accountId = research?.id

  const accountQ = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountApi.get(accountId),
    enabled: accountId !== undefined,
    refetchInterval: 10000,
  })
  const histories = useQueries({
    queries: HORIZON_WINDOWS.map(([, range]) => ({
      queryKey: ['equity-history', accountId, range],
      queryFn: () => accountApi.history(accountId, range),
      enabled: accountId !== undefined,
      retry: 1,
      staleTime: 300000,
    })),
  })
  const benchQ = useQuery({
    queryKey: ['benchmark-returns'],
    queryFn: () => benchmarkApi.returns(),
    retry: 1,
    staleTime: 60000,
    refetchInterval: 300000,
  })

  // A disabled query is never "loading", so an unresolved roster counts
  // explicitly (as PicksExcessModule).
  const booksUnresolved = booksPending || (research === undefined && !booksError)
  const isLoading = booksUnresolved || [accountQ, benchQ, ...histories].some(stillLoading)
  const isError = !isLoading && (research === undefined || unavailable(accountQ))

  const account = accountQ.data
  const acct = account
    ? { daily_pnl: Number(account.daily_pnl), account_value: Number(account.account_value) }
    : null
  const windowReturn = (tf: Timeframe, i: number) => {
    if (!acct) return { pct: null, flowAdjusted: true }
    const h = histories[i]?.data
    const netFlow = num(h?.net_flow)
    return bookWindowReturn(tf, acct, h?.points ?? [], netFlow)
  }
  const windows = HORIZON_WINDOWS.map(([key, tf], i) => ({ key, tf, ret: windowReturn(tf, i) }))
  const bookPct = {
    day: acct ? bookWindowReturn('DAY', acct, [], null).pct : null,
    ...Object.fromEntries(windows.map((w) => [w.key, w.ret.pct])),
  } as Record<'day' | '1w' | '1m' | '3m', number | null>
  // PicksPanel's caveats, per row: 1D vs a stale SPY day / last session, and
  // the windows whose return is raw equity change (net_flow unknown).
  const caveats = usePicksCaveats(benchQ.data)
  const rawWindows = windows.filter((w) => w.ret.pct !== null && !w.ret.flowAdjusted).map((w) => w.tf)
  const caption = [
    caveats.spyDayEnd ? `1D SPY ${caveats.spyDayEnd}` : null,
    caveats.marketClosed ? 'last session' : null,
    rawWindows.length > 0 ? `raw (no flows) ${rawWindows.join('/')}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Module
      title="Picks · excess vs SPY by horizon"
      caption={isLoading || isError || !caption ? undefined : caption}
      drawerKey="picks-horizons"
      span={5}
      isLoading={isLoading}
      isError={isError}
      errorText={research === undefined ? 'books unavailable' : 'picks unavailable'}
    >
      <HBarPair rows={excessByHorizon(benchQ.data, bookPct)} ariaLabel="research excess vs SPY by horizon" />
    </Module>
  )
}
